import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { BizAppConfig } from 'config/application.manifest';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { getAccessToken } from '@/services/oauth2';
import { errorConfig } from './requestErrorConfig';
import { getStoredNavTheme } from './utils/theme';
import '@ant-design/v5-patch-for-react-19';

const loginPath = '/user/login';
const isLocalHost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: { navTheme?: 'light' | 'realDark' };
  currentUser?: API.CurrentUser;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      // 先尝试从 localStorage 获取用户信息
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        console.log('📦 从 localStorage 加载用户信息');
        try {
          const user = JSON.parse(cachedUser);
          console.log('👤 缓存的用户信息:', user);
          return user;
        } catch (e) {
          console.error('解析缓存用户信息失败:', e);
          localStorage.removeItem('user');
        }
      }

      // 检查是否有 token，没有 token 就不要调用 API
      const token = getAccessToken();

      if (!token) {
        console.log('⚠️ 没有 access token，跳过获取用户信息');
        return undefined;
      }

      // 如果有 token 但没有缓存，从 API 获取
      console.log('📤 从 API 获取用户信息');
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      console.log('👤 API 返回的用户信息:', msg);

      // 保存到 localStorage
      if (msg) {
        localStorage.setItem('user', JSON.stringify(msg));
      }

      return msg;
    } catch (_error) {
      console.error('获取用户信息失败:', _error);
      // 不要在这里跳转到登录页，由请求层 401 拦截统一重定向
      return undefined;
    }
  };

  // 如果不是登录页面，执行
  const { location } = history;
  console.log('🚀 getInitialState - 当前路径:', location.pathname);

  if (
    ![
      loginPath,
      '/user/register',
      '/user/forgot-password',
      '/school-user/login',
      '/school-user/register',
      '/school-user/forgot-password',
      '/school-user/service-agreement',
      '/school-user/privacy-policy',
      '/user/privacy-policy',
      '/user/service-agreement',
      '/privacy-policy',
      '/service-agreement',
      '/callback',
    ].includes(location.pathname)
  ) {
    const currentUser = await fetchUserInfo();
    console.log('✅ Initial state 设置完成，用户:', currentUser);
    return {
      fetchUserInfo,
      currentUser,
      settings: { navTheme: getStoredNavTheme() || 'light' },
    };
  }

  console.log('⏭️ 跳过 fetchUserInfo（登录相关页面）');
  return {
    fetchUserInfo,
    settings: { navTheme: getStoredNavTheme() || 'light' },
  };
}

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 本地开发强制同源，配合 Umi proxy 避免 CORS
  baseURL: isLocalHost
    ? BizAppConfig.appApiUrlLocal
    : BizAppConfig.appApiUrlServer,
  ...errorConfig,
};
